import type { DesignSpec } from '@shared/spec';
import { firestore, storage } from '@/lib/firebase';
import { fetchFirebaseFunction } from '@/lib/firebaseFunctions';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  orderBy,
  query,
} from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface Checkpoint {
  id: string;
  parentCheckpointId: string | null;
  userMessageId: string;
  spec: DesignSpec;
  specHash: string;
  toolCallSummary: string;
  createdAt: string;
  authorUserId: string;
  thumbnailStoragePath?: string;
  thumbnailUrl?: string;
}

/** List all checkpoints for a conversation, oldest first. */
export function useCheckpoints(conversationId: string | undefined) {
  return useQuery<Checkpoint[]>({
    queryKey: ['checkpoints', conversationId],
    enabled: !!conversationId,
    queryFn: async () => {
      if (!conversationId) return [];
      const q = query(
        collection(firestore, 'conversations', conversationId, 'checkpoints'),
        orderBy('createdAt', 'asc'),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Checkpoint,
      );
      // Resolve thumbnail download URLs lazily. Firebase Storage does this
      // via a signed URL the first time it's asked.
      return Promise.all(
        rows.map(async (ckpt) => {
          if (!ckpt.thumbnailStoragePath) return ckpt;
          try {
            const url = await getDownloadURL(
              storageRef(storage, ckpt.thumbnailStoragePath),
            );
            return { ...ckpt, thumbnailUrl: url };
          } catch {
            return ckpt;
          }
        }),
      );
    },
  });
}

/** Rewind: point the conversation's currentCheckpointId at an existing
 *  checkpoint. The next chat turn will seed its working spec from there,
 *  and the resulting new checkpoint becomes a fork. */
export function useRewindToCheckpoint(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkpointId: string) => {
      if (!conversationId) throw new Error('conversationId required');
      await updateDoc(doc(firestore, 'conversations', conversationId), {
        currentCheckpointId: checkpointId,
        updated_at: new Date().toISOString(),
      });
      return checkpointId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['checkpoints', conversationId] });
    },
  });
}

/** Upload a PNG dataURL captured from the viewer as a checkpoint thumbnail. */
export async function uploadCheckpointThumbnail(params: {
  conversationId: string;
  checkpointId: string;
  dataUrl: string;
}): Promise<void> {
  const response = await fetchFirebaseFunction('writeCheckpointThumb', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`thumbnail upload failed (${response.status}): ${text}`);
  }
}
